import React, { useState, useEffect, useRef, Suspense } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { 
  Loader2, Store, Smartphone, Lock, QrCode, 
  User, Clock, AlertCircle, 
  ChevronRight, ChevronLeft, ArrowLeft, ShieldCheck, Mail, Key
} from 'lucide-react';
import { identityDomain } from '../domain/identity/IdentityDomainFactory';

import jsQR from 'jsqr';

// Lazy load QR Scanner to optimize initial load
const QRScannerOverlay = ({ onScan, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let animationFrameId;
    let stream;

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'environment' } 
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch {
        setError('Không thể truy cập camera. Vui lòng kiểm tra quyền.');
      }
    };

    const scan = () => {
      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d', { willReadFrequently: true });

        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.height = video.videoHeight;
          canvas.width = video.videoWidth;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert',
          });

          if (code) {
            onScan(code.data);
            return;
          }
        }
      }
      animationFrameId = requestAnimationFrame(scan);
    };

    startCamera().then(() => {
      animationFrameId = requestAnimationFrame(scan);
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center p-6">
      <div className="absolute top-6 right-6 z-10">
        <button onClick={onClose} className="p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="relative w-full max-w-sm aspect-square rounded-3xl overflow-hidden border-4 border-indigo-500 shadow-[0_0_30px_rgba(99,102,241,0.5)]">
        <video ref={videoRef} playsInline autoPlay muted className="w-full h-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />
        
        {/* Scanning line animation */}
        <div className="absolute inset-x-0 h-1 bg-indigo-500 shadow-[0_0_15px_rgba(99,102,241,1)] animate-scan-line top-0" />
        
        {/* HUD Overlay */}
        <div className="absolute inset-0 border-[40px] border-black/40" />
        <div className="absolute inset-[40px] border-2 border-dashed border-white/50 rounded-xl" />
      </div>

      <div className="mt-8 text-center">
        <h3 className="text-xl font-bold text-white mb-2">Đang quét mã QR</h3>
        <p className="text-slate-400">Vui lòng đưa mã nhân viên vào khung hình</p>
        {error && <p className="mt-4 text-red-400 font-medium">{error}</p>}
      </div>
    </div>
  );
};

const X = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export default function EntranceUI({ onLoginSuccess }) {
  const [activeTab, setActiveTab] = useState('owner'); // 'owner' | 'staff'
  const [staffMode, setStaffMode] = useState('menu'); // 'menu' | 'attendance' | 'pos'
  const [step, setStep] = useState(1); // 1: QR/Select, 2: PIN, 3: GPS/Photo
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // States for Staff Flow
  const [isQrActive, setIsQrActive] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [pinBuffer, setPinBuffer] = useState('');
  const [pinError, setPinError] = useState(false);
  const [locationId, setLocationId] = useState(localStorage.getItem('pos_location_id') || '');
  const [staffList, setStaffList] = useState([]);

  // States for Owner/Manager Flow
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isManagerLogin, setIsManagerLogin] = useState(false);

  // Load staff list if on staff tab
  useEffect(() => {
    if (activeTab === 'staff') {
      const loadStaff = async () => {
        const shopId = localStorage.getItem('pos_shop_id');
        if (!shopId) return;
        try {
          const { listenStaff, POS_CONFIG_APP_ID } = await import('../services/staffService');
          const unsub = listenStaff(POS_CONFIG_APP_ID, shopId, (data) => {
            setStaffList(data);
          });
          return () => unsub();
        } catch (e) { console.error(e); }
      };
      loadStaff();
    }
  }, [activeTab]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await identityDomain.authPort.loginWithProvider('google');
      // Redirect will happen inside the port
    } catch (err) {
      setError(err?.message || 'Lỗi đăng nhập Google');
    } finally {
      setLoading(false);
    }
  };

  const [isRegistering, setIsRegistering] = useState(false);

  const handleEmailLogin = async (event) => {
    if (event) event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const loginUseCase = identityDomain.getLoginUseCase();
      const session = await loginUseCase.execute({ email, password });
      if (onLoginSuccess) onLoginSuccess(session);
    } catch {
      setError('Email hoặc mật khẩu không chính xác.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (event) => {
    if (event) event.preventDefault();
    if (password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự.');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const authPort = identityDomain.authPort;
      await authPort.signUp({ email, password });
      
      // Auto-login after signup
      const loginUseCase = identityDomain.getLoginUseCase();
      const session = await loginUseCase.execute({ email, password });
      if (onLoginSuccess) onLoginSuccess(session);
    } catch (err) {
      setError(err.message || 'Đăng ký thất bại. Email có thể đã được sử dụng.');
    } finally {
      setLoading(false);
    }
  };

  const handleQrScan = (data) => {
    setIsQrActive(false);
    try {
      // Expecting JSON: {"staffId": "...", "locationId": "..."} or just staff ID
      let staffId = data;
      if (data.startsWith('{')) {
        const parsed = JSON.parse(data);
        staffId = parsed.staffId;
        if (parsed.locationId) {
          setLocationId(parsed.locationId);
          localStorage.setItem('pos_location_id', parsed.locationId);
        }
      }
      
      const staff = staffList.find(s => s.id === staffId || s.staffCode === staffId);
      if (staff) {
        setSelectedStaff(staff);
        setStep(2);
        setPinBuffer('');
      } else {
        setError('Không nhận diện được nhân viên. Vui lòng thử lại.');
      }
    } catch {
      setError('Mã QR không hợp lệ.');
    }
  };

  const handlePinInput = (val) => {
    if (val === 'back') {
      setPinBuffer(p => p.slice(0, -1));
      return;
    }
    const nextPin = pinBuffer + val;
    if (nextPin.length <= 4) {
      setPinBuffer(nextPin);
      setPinError(false);
      
      if (nextPin.length === 4) {
        // Simple verification against staffCode or pin field
        const targetPin = selectedStaff.pin || selectedStaff.staffCode;
        if (nextPin === String(targetPin).slice(-4)) {
          if (staffMode === 'attendance') {
            setStep(3); // Proceed to GPS/Photo
          } else {
            // POS Login - Special handling
            handlePosLogin(selectedStaff, nextPin);
          }
        } else {
          setPinError(true);
          setTimeout(() => setPinBuffer(''), 500);
        }
      }
    }
  };

  const handlePosLogin = async (staff, pin) => {
    setLoading(true);
    try {
      const shopId = localStorage.getItem('pos_shop_id');
      const phone = staff.phone || staff.phone_number;
      if (!phone) throw new Error('Nhân viên thiếu số điện thoại để đăng nhập POS.');
      
      const loginUseCase = identityDomain.getLoginUseCase();
      const session = await loginUseCase.execute({ shopId, phone, pin });
      
      if (onLoginSuccess) onLoginSuccess(session);
    } catch (e) {
      setError(e.message);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-4 bg-slate-950 text-white selection:bg-indigo-500/30">
      {/* Dynamic Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-600/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative w-full max-w-lg">
        {/* Main Card */}
        <Motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-slate-900/80 backdrop-blur-3xl rounded-[2.5rem] border border-white/10 shadow-2xl shadow-indigo-500/10 p-8 md:p-12 z-10 entrance-glass"
          style={{ 
            WebkitBackdropFilter: 'blur(32px)',
          }}
        >
          {/* Brand Header */}
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-800 shadow-xl shadow-indigo-600/30 mb-6">
              <Store className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-black tracking-tight text-white mb-2">Marketing AI Pro</h1>
            <p className="text-slate-400 font-medium">Hệ thống quản trị thông minh</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex p-1 bg-slate-800/50 rounded-2xl mb-8 border border-white/5">
            <button
              onClick={() => { setActiveTab('owner'); setStep(1); setStaffMode('menu'); }}
              className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'owner' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <User size={18} /> Chủ quán
            </button>
            <button
              onClick={() => { setActiveTab('staff'); setStep(1); setStaffMode('menu'); }}
              className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'staff' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <Smartphone size={18} /> Nhân viên
            </button>
          </div>

          {/* Content Area */}
          <AnimatePresence mode="wait">
            {activeTab === 'owner' ? (
              <Motion.div
                key="owner"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-6"
              >
                {isManagerLogin ? (
                <form onSubmit={isRegistering ? handleEmailSignUp : handleEmailLogin} className="space-y-6">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-white font-black uppercase tracking-widest text-sm">
                      {isRegistering ? 'Đăng ký hệ thống' : 'Đăng nhập email'}
                    </h3>
                    <button 
                      type="button"
                      onClick={() => setIsRegistering(!isRegistering)}
                      className="text-indigo-400 text-[10px] font-black uppercase tracking-widest hover:text-white transition-colors"
                    >
                      {isRegistering ? 'Về Đăng nhập' : 'Tạo tài khoản mới'}
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="relative group">
                      <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={20} />
                      <input
                        type="email"
                        placeholder="EMAIL CỦA BẠN"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-16 pl-14 pr-6 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50 focus:bg-indigo-500/5 transition-all font-bold tracking-widest text-sm"
                        required
                      />
                    </div>
                    <div className="relative group">
                      <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={20} />
                      <input
                        type="password"
                        placeholder="MẬT KHẨU"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-16 pl-14 pr-6 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50 focus:bg-indigo-500/5 transition-all font-bold tracking-widest text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center px-2">
                    {!isRegistering && (
                      <button type="button" className="text-slate-600 hover:text-indigo-400 text-[10px] font-black uppercase tracking-widest transition-colors">
                        Quên mật khẩu?
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setIsManagerLogin(false);
                        setIsRegistering(false);
                      }}
                      className="text-slate-600 hover:text-rose-400 text-[10px] font-black uppercase tracking-widest transition-colors ml-auto"
                    >
                      Quay lại
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-16 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-xl shadow-indigo-600/20 uppercase tracking-[0.2em] text-sm"
                  >
                    {loading ? 'Đang xử lý...' : (isRegistering ? 'Đăng ký ngay' : 'Đăng nhập ngay')}
                  </button>
                </form>
                ) : (
                  <div className="space-y-4">
                     <p className="text-center text-slate-500 text-sm mb-6">Đăng nhập tài khoản Chủ quán để quản lý thương hiệu và vận hành.</p>
                     <button
                      onClick={handleGoogleLogin}
                      disabled={loading}
                      className="w-full h-16 bg-white text-slate-900 rounded-2xl font-bold text-lg flex items-center justify-center gap-4 hover:bg-slate-100 transition-all shadow-xl shadow-white/5 disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="animate-spin text-slate-900" /> : <><GoogleIcon /> Tiếp tục với Google</>}
                    </button>
                    <button
                      onClick={() => setIsManagerLogin(true)}
                      className="w-full h-16 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-2xl font-bold flex items-center justify-center gap-3 transition-all"
                    >
                      <Mail size={20} className="text-indigo-400" /> Đăng nhập bằng Email
                    </button>

                    <div className="pt-4 text-center">
                       <button 
                         onClick={() => {
                           setIsManagerLogin(true);
                           setIsRegistering(true);
                         }}
                         className="text-indigo-400 hover:text-indigo-300 text-sm font-bold transition-colors"
                       >
                         Chưa có tài khoản? Đăng ký ngay
                       </button>
                    </div>
                  </div>
                )}
              </Motion.div>
            ) : (
              <Motion.div
                key="staff"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {step === 1 && staffMode === 'menu' && (
                  <div className="grid grid-cols-1 gap-4">
                    <p className="text-center text-slate-500 text-sm mb-4">Chào mừng đội ngũ nhân sự. Vui lòng chọn tác vụ cần thực hiện.</p>
                    <button
                      onClick={() => { setStaffMode('attendance'); }}
                      className="group flex items-center gap-4 p-6 bg-slate-800/50 hover:bg-indigo-600/20 border border-slate-700 hover:border-indigo-500 rounded-3xl transition-all"
                    >
                      <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Clock className="text-white" size={28} />
                      </div>
                      <div className="text-left">
                        <h4 className="font-bold text-lg text-white">Chấm công hàng ngày</h4>
                        <p className="text-slate-500 text-sm">Vào ca/Tan ca, xác thực GPS & Ảnh</p>
                      </div>
                      <ChevronRight size={20} className="ml-auto text-slate-600" />
                    </button>

                    <button
                      onClick={() => { setStaffMode('pos'); }}
                      className="group flex items-center gap-4 p-6 bg-slate-800/50 hover:bg-emerald-600/20 border border-slate-700 hover:border-emerald-500 rounded-3xl transition-all"
                    >
                      <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Store className="text-white" size={28} />
                      </div>
                      <div className="text-left">
                        <h4 className="font-bold text-lg text-white">Đăng nhập Bán hàng</h4>
                        <p className="text-slate-500 text-sm">Vào giao diện POS, xử lý đơn hàng</p>
                      </div>
                      <ChevronRight size={20} className="ml-auto text-slate-600" />
                    </button>
                  </div>
                )}

                {step === 1 && staffMode !== 'menu' && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-2">
                      <button onClick={() => setStaffMode('menu')} className="p-2 -ml-2 text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft size={20} />
                      </button>
                      <h3 className="font-bold text-lg">
                        {staffMode === 'attendance' ? 'Xác thực Chấm công' : 'Xác thực vào POS'}
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <button
                        onClick={() => setIsQrActive(true)}
                        className="flex flex-col items-center justify-center p-8 bg-indigo-600 hover:bg-indigo-700 rounded-3xl gap-4 shadow-xl shadow-indigo-600/20 transition-all border-b-4 border-indigo-900 active:translate-y-1"
                      >
                        <QrCode size={48} className="text-white" />
                        <span className="font-black text-xl">QUÉT MÃ QR</span>
                      </button>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-800"></div></div>
                        <div className="relative flex justify-center text-xs uppercase"><span className="bg-slate-900 px-3 text-slate-600 font-bold tracking-[0.2em]">Hoặc chọn thủ công</span></div>
                      </div>

                      <div className="max-h-48 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                        {staffList.filter(s => s.status === 'active').map(s => (
                          <button
                            key={s.id}
                            onClick={() => { setSelectedStaff(s); setStep(2); setPinBuffer(''); }}
                            className="w-full flex items-center gap-3 p-3 bg-slate-800/40 hover:bg-slate-800 border border-slate-700 rounded-xl transition-all group"
                          >
                            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center font-bold text-slate-400">
                              {s.name?.[0] || 'N'}
                            </div>
                            <span className="font-bold text-slate-300 group-hover:text-white transition-colors">{s.name}</span>
                            <ChevronRight size={16} className="ml-auto text-slate-600" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && selectedStaff && (
                  <div className="space-y-8 py-4">
                    <div className="text-center">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Xác thực mã PIN</p>
                      <h3 className="text-2xl font-black text-white">{selectedStaff.name}</h3>
                    </div>

                    {/* PIN Dots */}
                    <div className="flex justify-center gap-4">
                       {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className={`w-4 h-4 rounded-full border-2 transition-all duration-300 ${
                            i < pinBuffer.length 
                              ? 'bg-indigo-500 border-indigo-500 scale-125 shadow-[0_0_15px_rgba(99,102,241,0.6)]' 
                              : 'border-slate-700 bg-slate-900'
                          } ${pinError ? 'bg-red-500 border-red-500 animate-shake' : ''}`}
                        />
                      ))}
                    </div>

                    {/* Numpad */}
                    <div className="grid grid-cols-3 gap-3 max-w-[300px] mx-auto">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                        <button
                          key={num}
                          type="button"
                          onClick={() => handlePinInput(num.toString())}
                          className="h-16 rounded-2xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-2xl font-bold flex items-center justify-center transition-all active:scale-90"
                        >
                          {num}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => { setStep(1); setSelectedStaff(null); }}
                        className="h-16 rounded-2xl bg-slate-900/40 hover:bg-slate-800 text-slate-500 font-bold flex items-center justify-center transition-all"
                      >
                        Hủy
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePinInput('0')}
                        className="h-16 rounded-2xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-2xl font-bold flex items-center justify-center transition-all active:scale-90"
                      >
                        0
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePinInput('back')}
                        className="h-16 rounded-2xl bg-slate-900/40 hover:bg-slate-800 border border-slate-800 text-slate-500 font-bold flex items-center justify-center transition-all active:scale-90"
                      >
                        <ChevronLeft />
                      </button>
                    </div>

                    {pinError && <p className="text-center text-red-500 font-bold text-sm">Sai mã PIN. Vui lòng thử lại.</p>}
                  </div>
                )}

                {step === 3 && staffMode === 'attendance' && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                      <h3 className="text-xl font-bold">Xác thực bảo mật</h3>
                      <p className="text-slate-400 text-sm">Đang hoàn tất quá trình chấm công cho {selectedStaff.name}</p>
                    </div>

                    {/* This would be the GPS/Photo UI - for brevity, I'll redirect or use a streamlined version */}
                    <div className="bg-slate-800/50 p-6 rounded-3xl border border-slate-700 text-center">
                      <p className="text-slate-500 mb-6 italic">Tiếp tục bước xác thực vị trí và ảnh tại quán...</p>
                      <button
                        onClick={() => {
                          // Redirect to full CheckInPortal with context
                          window.location.href = `/checkin?staffId=${selectedStaff.id}&locationId=${locationId}`;
                        }}
                        className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg"
                      >
                        Mở cổng Chấm công <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </Motion.div>
            )}
          </AnimatePresence>

          {error && (
            <div className="mt-8 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm font-bold flex items-center gap-3 animate-shake">
              <AlertCircle size={20} className="shrink-0" /> {error}
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-white/5 text-center">
             <p className="text-slate-600 text-xs font-medium">© 2024 Marketing AI Pro · Powered by 2C Marketing</p>
          </div>
        </Motion.div>
      </div>

      {isQrActive && (
        <Suspense fallback={<div className="fixed inset-0 bg-black z-[100] flex items-center justify-center text-white">Đang tải...</div>}>
          <QRScannerOverlay onScan={handleQrScan} onClose={() => setIsQrActive(false)} />
        </Suspense>
      )}

      <style>{`
        /* Override aggressive global mobile stabilization that forces white background */
        @media (max-width: 768px) {
          .entrance-glass {
            backdrop-filter: blur(24px) !important;
            -webkit-backdrop-filter: blur(24px) !important;
            background-color: rgba(15, 23, 42, 0.85) !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
          }
        }
        
        @keyframes scan-line {
          0% { top: 0; }
          100% { top: 100%; }
        }
        .animate-scan-line {
          animation: scan-line 3s linear infinite;
        }
        .animate-shake {
          animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
        }
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(99, 102, 241, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(99, 102, 241, 0.5);
        }
      `}</style>
    </div>
  );
}

